import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, of } from 'rxjs';
import { WnPerms } from '../../../../../core/auth/worknest-admin-permissions';
import type { AssetCharge } from '../../../../../core/models/assets/asset-charges.dto';
import { AssetChargesService } from '../../../../../core/services/asset-charges.service';
import { PermissionService } from '../../../../../core/services/permission.service';
import { StatusChip } from '../../../../../shared/components/status-chip/status-chip';

/** The charges raised against one asset, shown on the asset detail page to anyone who may view charges. */
@Component({
  selector: 'app-asset-charges-panel',
  standalone: true,
  imports: [RouterLink, TranslatePipe, StatusChip],
  templateUrl: './asset-charges-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetChargesPanelComponent {
  private readonly charges = inject(AssetChargesService);
  private readonly permissions = inject(PermissionService);

  readonly assetId = input.required<string>();
  protected readonly canView = computed(() => this.permissions.hasAny([...WnPerms.assets.chargeView, ...WnPerms.assets.chargeManage]));
  protected readonly rows = signal<AssetCharge[]>([]);

  constructor() {
    effect((onCleanup) => {
      const id = this.assetId();
      if (!this.canView()) return;
      const sub = this.charges
        .list({ assetId: id })
        .pipe(catchError(() => of({ items: [], totalCount: 0 })))
        .subscribe((page) => this.rows.set(page.items ?? []));
      onCleanup(() => sub.unsubscribe());
    });
  }
}
